import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap, Loader2 } from "lucide-react";
import { useState } from "react";
import { useCompletedCourses } from "@/hooks/useCompletedCourses";
import { toast } from "@/hooks/use-toast";

const anosRecentes = ["2026", "2025", "2024", "2023", "2022", "2021", "Antes de 2021"];

export default function AlunoCursoNovo() {
  const navigate = useNavigate();
  const { createCourse } = useCompletedCourses();
  const [nome, setNome] = useState("");
  const [provedor, setProvedor] = useState("");
  const [ano, setAno] = useState("");
  const [link, setLink] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!nome || !provedor || !ano) {
      toast({ title: "Campos obrigatórios", description: "Preencha nome, provedor e ano.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await createCourse.mutateAsync({
        course_name: nome,
        provider: provedor,
        year_label: ano,
        url: link || undefined,
        notes: notes || undefined,
      });
      toast({ title: "Curso adicionado!" });
      navigate("/aluno/experiencia/cursos");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/experiencia/cursos")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground">Adicionar Curso</h1>
          <p className="text-muted-foreground text-sm">Registre um curso que você completou</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-5">
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Nome do curso *</label>
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Fundamentos de UX Design" />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Provedor/Plataforma *</label>
          <Input value={provedor} onChange={(e) => setProvedor(e.target.value)} placeholder="Ex: Coursera, Udemy, Google, Senac..." />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Ano de conclusão *</label>
          <div className="flex flex-wrap gap-2">
            {anosRecentes.map((a) => (
              <button key={a} onClick={() => setAno(a)}
                className={`px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${
                  ano === a ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/50"
                }`}>{a}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Link do certificado (opcional)</label>
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Notas (opcional)</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="O que você aprendeu neste curso?"
            rows={2} className="resize-none" />
        </div>

        <div className="bg-success/10 rounded-xl p-4 border border-success/20">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="w-5 h-5 text-success" />
            <p className="text-sm text-foreground font-medium">Sem upload de certificados</p>
          </div>
          <p className="text-xs text-muted-foreground">Valorizamos sua trajetória e aprendizado.</p>
        </div>

        <Button variant="hero" size="xl" className="w-full" onClick={handleSubmit} disabled={saving}>
          {saving ? (<><Loader2 className="w-5 h-5 animate-spin" />Salvando...</>) : "Adicionar Curso"}
        </Button>
      </motion.div>
    </div>
  );
}
